import { create } from 'zustand';

import type { IconName } from '@/components/ui/icon-paths';
import { randomId } from '@/lib/random-id';

export type ToastTone = 'success' | 'error' | 'info';

export interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  /** Milliseconds before auto-dismiss; 0 keeps it until dismissed. */
  durationMs: number;
}

interface ToastState {
  toasts: Toast[];
  push: (toast: Omit<Toast, 'id' | 'durationMs'> & { durationMs?: number }) => void;
  dismiss: (id: string) => void;
}

export const TONE_STYLES: Record<ToastTone, { icon: IconName; className: string }> = {
  success: { icon: 'check-circle', className: 'border-success/30 bg-success-soft text-success' },
  error: { icon: 'alert-circle', className: 'border-danger/30 bg-danger-soft text-danger' },
  info: { icon: 'info', className: 'border-primary/30 bg-primary-soft text-primary' },
};

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],

  push: (toast) => {
    const id = randomId('toast');

    set((state) => ({
      toasts: [
        ...state.toasts,
        /* Errors linger: they usually need reading and acting on, where a
           success message only needs noticing. */
        { id, durationMs: toast.tone === 'error' ? 8000 : 4000, ...toast },
      ],
    }));
  },

  dismiss: (id) => {
    set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) }));
  },
}));

/**
 * Fire-and-forget notifications, callable from anywhere - including outside a
 * component, which is why this reads the store directly rather than being a
 * hook.
 *
 * ```ts
 * toast.success('Tenant created');
 * toast.error('Could not save', error.message);
 * ```
 */
export const toast = {
  success: (title: string, description?: string): void => {
    useToastStore.getState().push({
      tone: 'success',
      title,
      ...(description ? { description } : {}),
    });
  },
  error: (title: string, description?: string): void => {
    useToastStore.getState().push({
      tone: 'error',
      title,
      ...(description ? { description } : {}),
    });
  },
  info: (title: string, description?: string): void => {
    useToastStore.getState().push({
      tone: 'info',
      title,
      ...(description ? { description } : {}),
    });
  },
};
